import { Tooltip } from 'primereact/tooltip';
import {
  WEEKLY_PACE_TOP_CATEGORY,
  WeeklyPaceColumn,
  WeeklyPaceSummary,
} from '../../../businessLogicService/weeklyPaceColumns';

interface Props {
  summary: WeeklyPaceSummary;
  onSelectColumn: (column: WeeklyPaceColumn) => void;
}

const CHART_HEIGHT_PX = 190;
const MAX_BAR_HEIGHT_PX = 140;
// A category with a single student still has to be visible and large enough to click.
const MIN_BAR_HEIGHT_PX = 40;

// Shared hook for one Tooltip instance covering every bar, rather than one instance per column.
const BAR_TOOLTIP_TARGET = 'nc_weekly-pace-bar';

const studentCountLabel = (students: number): string => `${students} student${students === 1 ? '' : 's'}`;

const categoryDescription = (column: WeeklyPaceColumn): string => {
  if (column.category === 0) return 'no new skills';
  if (column.category === WEEKLY_PACE_TOP_CATEGORY) return `${WEEKLY_PACE_TOP_CATEGORY} or more new skills`;

  return `${column.category} new skill${column.category === 1 ? '' : 's'}`;
};

const GoalMarker = (): JSX.Element => (
  <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-self-stretch">
    <span className="tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-solid tw-border-amber-200 tw-bg-neutral-0 tw-px-3 tw-py-1 tw-font-atkinson tw-text-xs tw-font-bold tw-text-neutral-950">
      Goal met
    </span>
    <div className="tw-w-0 tw-flex-1 tw-border-y-0 tw-border-r-0 tw-border-l-2 tw-border-dashed tw-border-amber-300" />
  </div>
);

export const WeeklyPaceChart = ({ summary, onSelectColumn }: Props): JSX.Element => {
  const mostStudentsInAColumn = Math.max(...summary.columns.map(column => column.students));
  const columnsBelowGoal = summary.columns.filter(column => !column.meetsGoal);
  const columnsAtGoal = summary.columns.filter(column => column.meetsGoal);

  const renderColumn = (column: WeeklyPaceColumn): JSX.Element => {
    const barHeight =
      MIN_BAR_HEIGHT_PX + (column.students / mostStudentsInAColumn) * (MAX_BAR_HEIGHT_PX - MIN_BAR_HEIGHT_PX);

    return (
      <div key={column.category} className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-gap-2">
        <button
          // Shared by every bar on purpose: PostHog groups the clicks under the one id, and the
          // label under the bar says which category it was. The aria-label cannot: PostHog's
          // element_attribute_ignorelist drops it.
          id="paceBar"
          type="button"
          data-pr-tooltip={studentCountLabel(column.students)}
          aria-label={`${studentCountLabel(column.students)} with ${categoryDescription(column)}. View students.`}
          onClick={() => onSelectColumn(column)}
          style={{ height: `${barHeight}px` }}
          className={`${BAR_TOOLTIP_TARGET} tw-w-full tw-cursor-pointer tw-rounded-lg tw-border-2 tw-border-solid ${
            column.meetsGoal
              ? 'tw-border-green-600 tw-bg-green-600'
              : 'tw-border-neutral-100 tw-bg-neutral-50 hover:tw-bg-neutral-100'
          }`}
        />
        <p className="tw-m-0 tw-font-atkinson tw-text-sm tw-font-bold tw-text-neutral-950">
          {column.label}
          {column.unit && <span className="tw-ml-1 tw-font-normal tw-text-neutral-600">{column.unit}</span>}
        </p>
      </div>
    );
  };

  return (
    // Inset horizontally: the bars are solid blocks that otherwise run flush to the card border.
    <div className="tw-w-full tw-px-4">
      <Tooltip className="tw-font-atkinson" target={`.${BAR_TOOLTIP_TARGET}`} position="top" />
      <div className="tw-flex tw-w-full tw-items-end tw-gap-4" style={{ height: `${CHART_HEIGHT_PX}px` }}>
        {columnsBelowGoal.map(renderColumn)}
        {/* It divides the two groups, so with nobody at the goal it would trail the chart marking
            off an empty stretch. */}
        {columnsAtGoal.length > 0 && <GoalMarker />}
        {columnsAtGoal.map(renderColumn)}
      </div>
    </div>
  );
};
